<template>
  <div class="sidebar-wrapper">
    <!-- Mobile Overlay -->
    <div v-if="mobileMenuOpen" class="sidebar-overlay" @click="closeMobileMenu"></div>

    <!-- Sidebar -->
    <aside class="sidebar" :class="{ open: mobileMenuOpen, collapsed: sidebarCollapsed }">
      <!-- Logo Section -->
      <div class="sidebar-header">
        <router-link to="/dashboard" class="logo-link" @click="closeMobileMenu">
          <img
            v-if="!sidebarCollapsed"
            alt="Carenuity logo"
            src="./../../assets/logo.svg"
            class="logo-img"
          />
          <img
            v-else
            alt="Carenuity logo"
            src="./../../assets/CarenuityLogoTransparent.png"
            class="logo-img logo-collapsed"
          />
          <!-- <span class="logo-text">Carenuity</span> -->
        </router-link>
        <!-- Collapse Toggle Button in Header -->
        <button
          class="collapse-toggle-header"
          @click="toggleSidebarCollapse"
          :title="sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            class="collapse-icon"
            :class="{ rotated: sidebarCollapsed }"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
      </div>

      <!-- Navigation Links -->
      <nav class="sidebar-nav">
        <ul class="nav-list">
          <li class="nav-item">
            <router-link to="/dashboard" class="nav-link" active-class="active" @click="closeMobileMenu">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="nav-icon">
                <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
              </svg>
              <span class="nav-text">Dashboard</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link to="/alarms" class="nav-link" active-class="active" @click="closeMobileMenu">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="nav-icon">
                <path
                  d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z" />
              </svg>
              <span class="nav-text">Alarms</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link to="/devices" class="nav-link" active-class="active" @click="closeMobileMenu">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                class="nav-icon">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              <span class="nav-text">Devices</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link to="/advanced-dashboard" class="nav-link" active-class="active" @click="closeMobileMenu">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                class="nav-icon">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span class="nav-text">Advanced Dashboard</span>
            </router-link>
          </li>
        </ul>
      </nav>

      <!-- External Links Section -->
      <div class="external-links">
        <!-- Solution Builder Link -->
        <a
          href="https://solutions.carenuity.com"
          target="_blank"
          rel="noopener noreferrer"
          class="external-link"
          title="Solution Builder"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="external-icon">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
          <span v-if="!sidebarCollapsed" class="external-text">Solution Builder</span>
        </a>

        <!-- Home Challenge Link -->
        <a
          href="https://carenuity.com/home-challenge/"
          target="_blank"
          rel="noopener noreferrer"
          class="external-link"
          title="Home Challenge"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="external-icon">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
          <span v-if="!sidebarCollapsed" class="external-text">Home Challenge</span>
        </a>
      </div>

      <!-- User Profile Section -->
      <div class="sidebar-footer">
        <user-profile-dropdown :collapsed="sidebarCollapsed" />
      </div>
    </aside>

    <!-- Mobile Menu Toggle -->
    <button class="mobile-toggle" @click="toggleMobileMenu" :class="{ active: mobileMenuOpen }"
      aria-label="Toggle navigation">
      <span></span>
      <span></span>
      <span></span>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import UserProfileDropdown from './UserProfileDropdown.vue';

/**
 * TheSidebar Component
 *
 * Modern glass-effect left sidebar navigation
 * Features:
 * - Fixed left sidebar with glass morphism effect
 * - Green accents on white/translucent background
 * - Collapsible sidebar (icon-only mode)
 * - Responsive mobile drawer
 * - Smooth animations and hover effects
 */
export default defineComponent({
  name: 'TheSidebar',
  components: {
    UserProfileDropdown
  },
  setup() {
    const route = useRoute();
    const mobileMenuOpen = ref(false);
    const sidebarCollapsed = ref(false);

    const toggleMobileMenu = (): void => {
      mobileMenuOpen.value = !mobileMenuOpen.value;
    };

    const closeMobileMenu = (): void => {
      mobileMenuOpen.value = false;
    };

    const toggleSidebarCollapse = (): void => {
      sidebarCollapsed.value = !sidebarCollapsed.value;
      // Persist state to localStorage
      localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed.value));
      // Emit custom event for App.vue to adjust layout
      window.dispatchEvent(new CustomEvent('sidebar-toggle', {
        detail: { collapsed: sidebarCollapsed.value }
      }));
    };

    // Load collapsed state from localStorage on mount
    onMounted(() => {
      const saved = localStorage.getItem('sidebarCollapsed');
      if (saved !== null) {
        sidebarCollapsed.value = JSON.parse(saved);
        // Emit initial state
        window.dispatchEvent(new CustomEvent('sidebar-toggle', {
          detail: { collapsed: sidebarCollapsed.value }
        }));
      }
    });

    // Watch route changes to close mobile menu
    watch(() => route.path, () => {
      closeMobileMenu();
    });

    return {
      mobileMenuOpen,
      sidebarCollapsed,
      toggleMobileMenu,
      closeMobileMenu,
      toggleSidebarCollapse
    };
  }
});
</script>

<style scoped>
/* ============================================
   Sidebar Wrapper
   ============================================ */
.sidebar-wrapper {
  position: relative;
  z-index: 1000;
}

/* ============================================
   Sidebar Base - Glass Effect
   ============================================ */
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 230px;
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border-right: 2px solid var(--color-primary);
  box-shadow: var(--shadow-sidebar);
  display: flex;
  flex-direction: column;
  z-index: var(--z-index-sidebar);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Collapsed State */
.sidebar.collapsed {
  width: 80px;
}

.sidebar.collapsed .sidebar-header {
  flex-direction: column;
  padding: var(--spacing-4);
  gap: var(--spacing-2);
}

.sidebar.collapsed .logo-link {
  width: 100%;
  justify-content: center;
}

.sidebar.collapsed .nav-text {
  opacity: 0;
  width: 0;
  overflow: hidden;
  white-space: nowrap;
}

.sidebar.collapsed .nav-link {
  justify-content: center;
  padding: var(--spacing-1) var(--spacing-1) var(--spacing-1) var(--spacing-5);
}

.sidebar.collapsed .nav-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.sidebar.collapsed .logo-img {
  margin: 0 auto;
  width: 45px;
  height: 45px;
}

.sidebar.collapsed .collapse-toggle-header {
  width: 100%;
}

/* ============================================
   Sidebar Header - Logo Section
   ============================================ */
.sidebar-header {
  padding: var(--spacing-8) var(--spacing-6);
  border-bottom: 2px solid var(--color-primary-alpha-15);
  background: linear-gradient(135deg, var(--color-primary-alpha-5) 0%, var(--color-primary-alpha-5) 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3);
}

.logo-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  text-decoration: none;
  transition: var(--transition-base);
  flex: 1;
}

.logo-link:hover {
  transform: translateX(5px);
}

.logo-img {
  height: 45px;
  width: auto;
  filter: drop-shadow(0 2px 8px var(--color-primary-alpha-30));
}

.logo-collapsed {
  height: 40px;
  width: 40px;
  object-fit: contain;
}

/* Collapse Toggle in Header */
.collapse-toggle-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2);
  background: transparent;
  border: 2px solid var(--color-primary-alpha-20);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-base);
  color: var(--color-primary);
  flex-shrink: 0;
}

.collapse-toggle-header:hover {
  background: var(--color-primary-alpha-10);
  border-color: var(--color-primary);
  transform: scale(1.05);
}

.collapse-toggle-header .collapse-icon {
  width: 20px;
  height: 20px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.collapse-toggle-header .collapse-icon.rotated {
  transform: rotate(180deg);
}

.logo-text {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ============================================
   Sidebar Navigation
   ============================================ */
.sidebar-nav {
  flex: 1;
  padding: var(--spacing-6) var(--spacing-4);
  overflow-y: auto;
  overflow-x: hidden;
}

/* Custom scrollbar */
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: var(--color-primary-alpha-5);
  border-radius: 3px;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: var(--color-primary-alpha-30);
  border-radius: 3px;
  transition: var(--transition-base);
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: var(--color-primary-alpha-50);
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
}

.nav-item {
  margin: 0;
}

/* ============================================
   Navigation Links - Modern Style
   ============================================ */
.nav-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  padding: var(--spacing-4) var(--spacing-5);
  color: var(--color-text-primary);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  border-radius: var(--radius-lg);
  border: 2px solid transparent;
  transition: var(--transition-base);
  position: relative;
  overflow: hidden;
}

.nav-link::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--gradient-primary);
  transform: scaleY(0);
  transition: var(--transition-base);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}

.nav-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  transition: var(--transition-base);
  color: var(--color-primary);
}

.nav-text {
  flex: 1;
  text-align: left;
  transition: var(--transition-base);
}

/* Focus State - Remove default outline */
.nav-link:focus {
  outline: none;
}

.nav-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--color-primary-alpha-30);
}

/* Hover State */
.nav-link:hover {
  background: var(--color-primary-alpha-8);
  border-color: var(--color-primary-alpha-20);
  transform: translateX(5px);
  box-shadow: var(--shadow-primary-md);
}

.nav-link:hover .nav-icon {
  transform: scale(1.1) rotate(-5deg);
  filter: drop-shadow(0 2px 4px var(--color-primary-alpha-40));
}

.nav-link:hover .nav-text {
  transform: translateX(3px);
}

/* Active State */
.nav-link.active {
  background: linear-gradient(135deg, var(--color-primary-alpha-15) 0%, var(--color-primary-alpha-10) 100%);
  border-color: var(--color-primary);
  color: var(--color-primary-dark);
  font-weight: var(--font-weight-bold);
  box-shadow: var(--shadow-primary-lg);
}

.nav-link.active::before {
  transform: scaleY(1);
}

.nav-link.active .nav-icon {
  color: var(--color-primary-dark);
  transform: scale(1.15);
  filter: drop-shadow(0 3px 6px var(--color-primary-alpha-50));
}

/* ============================================
   External Links Section
   ============================================ */
.external-links {
  padding: var(--spacing-3) var(--spacing-4);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  border-top: 2px solid var(--color-primary-alpha-15);
}

.external-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: var(--spacing-3) var(--spacing-4);
  text-decoration: none;
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  border-radius: var(--radius-lg);
  border: 2px solid transparent;
  transition: var(--transition-base);
  position: relative;
  overflow: hidden;
}

.external-link::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--gradient-primary);
  transform: scaleY(0);
  transition: var(--transition-base);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}

.external-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  transition: var(--transition-base);
  color: var(--color-primary);
}

.external-text {
  flex: 1;
  text-align: left;
  transition: var(--transition-base);
  white-space: nowrap;
}

.external-link:hover {
  background: var(--color-primary-alpha-8);
  border-color: var(--color-primary-alpha-20);
  transform: translateX(5px);
  box-shadow: var(--shadow-primary-sm);
}

.external-link:hover::before {
  transform: scaleY(1);
}

.external-link:hover .external-icon {
  transform: scale(1.1);
  filter: drop-shadow(0 2px 4px var(--color-primary-alpha-40));
}

.external-link:hover .external-text {
  transform: translateX(3px);
}

/* Collapsed State for External Links */
.sidebar.collapsed .external-links {
  padding: var(--spacing-3) var(--spacing-2);
}

.sidebar.collapsed .external-link {
  justify-content: center;
  padding: var(--spacing-3);
}

.sidebar.collapsed .external-text {
  display: none;
}

/* ============================================
   Sidebar Footer - User Profile
   ============================================ */
.sidebar-footer {
  padding: var(--spacing-6) var(--spacing-4);
  border-top: 2px solid var(--color-primary-alpha-15);
  background: linear-gradient(135deg, var(--color-primary-alpha-5) 0%, var(--color-primary-alpha-5) 100%);
}

/* ============================================
   Mobile Overlay
   ============================================ */
.sidebar-overlay {
  display: none;
}

/* ============================================
   Mobile Toggle Button
   ============================================ */
.mobile-toggle {
  display: none;
  position: fixed;
  top: var(--spacing-4);
  left: var(--spacing-4);
  z-index: calc(var(--z-index-sidebar) + 1);
  flex-direction: column;
  gap: 5px;
  background: var(--gradient-primary);
  border: 2px solid white;
  border-radius: var(--radius-lg);
  cursor: pointer;
  padding: var(--spacing-3);
  box-shadow: var(--shadow-primary-lg);
  transition: var(--transition-base);
}

.mobile-toggle:hover {
  transform: scale(1.05);
  box-shadow: var(--shadow-primary-xl);
}

.mobile-toggle span {
  width: 24px;
  height: 3px;
  background: white;
  border-radius: 2px;
  transition: var(--transition-base);
}

.mobile-toggle.active span:nth-child(1) {
  transform: rotate(45deg) translate(7px, 7px);
}

.mobile-toggle.active span:nth-child(2) {
  opacity: 0;
  transform: translateX(-10px);
}

.mobile-toggle.active span:nth-child(3) {
  transform: rotate(-45deg) translate(7px, -7px);
}

/* ============================================
   Responsive Design - Mobile
   ============================================ */
@media (max-width: 968px) {
  .sidebar {
    transform: translateX(-100%);
    box-shadow: none;
  }

  .sidebar.open {
    transform: translateX(0);
    box-shadow: var(--shadow-sidebar-mobile);
  }

  .sidebar-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--color-overlay-dark);
    backdrop-filter: var(--backdrop-blur-sm);
    z-index: calc(var(--z-index-sidebar) - 1);
    animation: fadeIn 0.3s ease;
  }

  .mobile-toggle {
    display: flex;
  }
}

/* ============================================
   Animations
   ============================================ */
@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* ============================================
   Print Styles
   ============================================ */
@media print {

  .sidebar,
  .mobile-toggle {
    display: none;
  }
}
</style>
